import { createColumnHelper, flexRender, useTable } from "@tanstack/react-table"
import { ButtonLink } from "design-system/atoms/Button"
import DateDisplay from "design-system/molecules/DateDisplay"
import {
  Table,
  TableBody,
  TableCell,
  TableEmptySlate,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
} from "design-system/molecules/Table"
import { cn } from "design-system/utils"
import { features, type Features } from "lib/table"
import { Trash } from "lucide-react"
import { useOutlookIntegrationsQuery } from "modules/settings/hooks/outlook/useOutlookIntegrationsQuery"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { OutlookIntegrationsResponseDataSchema } from "schema"
import { z } from "zod"

const columnHelper = createColumnHelper<
  Features,
  z.infer<typeof OutlookIntegrationsResponseDataSchema>[number]
>()

export const OutlookIntegrationsTable = () => {
  const { t } = useTranslation()
  const { data, isPending } = useOutlookIntegrationsQuery()
  const columns = useMemo(
    () =>
      columnHelper.columns([
        columnHelper.accessor((row) => row.outlookAccount.email, {
          id: "account",
          header: t("settings.integrations.outlook.table.account"),
          meta: { colSpan: 2 },
        }),
        columnHelper.accessor("email", {
          header: t("settings.integrations.outlook.table.username"),
          meta: { colSpan: 2 },
        }),
        columnHelper.accessor("updatedAt", {
          header: t("settings.integrations.outlook.table.updatedAt"),
          cell: (props) => <DateDisplay date={props.getValue()} />,
          meta: { colSpan: 1 },
        }),
        columnHelper.display({
          header: "",
          id: "actions",
          meta: { align: "right" },
          cell: (props) => {
            return (
              <ButtonLink
                size={"icon"}
                variant={"ghost"}
                to={"/settings/channels/outlook/deleteIntegration/$id"}
                params={{ id: props.row.original.id }}
              >
                <Trash />
              </ButtonLink>
            )
          },
        }),
      ]),
    [t],
  )

  const table = useTable({
    data: data ?? [],
    columns,
    features,
  })

  function renderContent() {
    if (isPending || data === undefined) {
      return <TableSkeleton colSpans={columns.map((c) => c.meta?.colSpan ?? 1)} />
    }

    if (data.length === 0) {
      return (
        <TableEmptySlate
          colSpan={columns.reduce((acc, c) => acc + (c.meta?.colSpan ?? 1), 0)}
          message={t("settings.integrations.outlook.table.noIntegrations")}
        />
      )
    }

    return table.getRowModel().rows.map((row) => (
      <TableRow key={row.original.id}>
        {row.getVisibleCells().map((cell) => (
          <TableCell
            key={cell.id}
            className={cn(cell.column.columnDef.meta?.className)}
            align={cell.column.columnDef.meta?.align}
            {...cell.column.columnDef.meta}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
    ))
  }

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead key={header.id} {...header.column.columnDef.meta}>
                {flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody className="**:data-[slot=table-cell]:first:w-8">{renderContent()}</TableBody>
    </Table>
  )
}
